import { FC, memo, useContext, useMemo } from 'react';

import { Button, ILabelProps, Label, Search } from '@smartshell/ui-kit';

import { LocalizationContext, TLocalesKeys } from 'localization';
import { SettingsContext } from 'settings';
import { ETariffType } from 'types';
import { assumeDuration, formatSum, getLabelProps } from 'utils';

import { TTariff } from '../interfaces';
import s from './TariffField.module.scss';

export type TTariffFieldProps = {
  value: number | null;
  onChange: (value: number | null) => void;
  tariffs: Array<TTariff>;
  disabled: boolean;
};

export const TariffField: FC<TTariffFieldProps> = memo(
  ({ value, onChange, tariffs, disabled }) => {
    const { t } = useContext(LocalizationContext);
    const {
      state: {
        settings: {
          currency: { sign: currency },
        },
      },
    } = useContext(SettingsContext);

    const selectedTariff = tariffs.find((tariff) => tariff.id === value);

    const labelProps = useMemo<
      (Omit<ILabelProps, 'text'> & { locale: TLocalesKeys }) | undefined
    >(() => {
      if (!selectedTariff) return undefined;

      return getLabelProps(
        selectedTariff.isPackage ? ETariffType.PACKET : ETariffType.FIXED
      );
    }, [selectedTariff]);
    return (
      <div>
        {selectedTariff ? (
          <div className={s.entity}>
            <div className={s.data}>
              <span className={s.title}>{selectedTariff.title}</span>
              <div className={s.info}>
                <div className={s.price}>
                  <span>
                    {formatSum(selectedTariff.price, currency, {
                      maximumFractionDigits: 2,
                    })}
                  </span>
                  {selectedTariff.isPackage
                    ? t('until', selectedTariff.duration)
                    : assumeDuration(Number(selectedTariff.duration), t)?.title}
                </div>
                {labelProps && (
                  <Label {...labelProps} text={t(labelProps.locale)} rounded />
                )}
              </div>
            </div>

            <Button
              iconName="CloseIcon"
              color="red"
              variant="ghost"
              size="s"
              onlyIcon
              onClick={() => onChange(null)}
              className={s.remove}
            />
          </div>
        ) : (
          <Search
            name="tariff"
            value={value}
            onChange={onChange}
            items={tariffs.map((tariff) => ({
              value: tariff.id,
              title: tariff.title,
            }))}
            placeholder={t('combo.tariff.add')}
            disabled={disabled}
            notFoundText={t('tariffs.notFound')}
            autocomplete="off"
            variant="secondary"
            bordered
          />
        )}
      </div>
    );
  }
);
